import { Asset, AssetClassCode, AssetsSearchResult } from '@/types/assets';
import { supabase } from '../SupabaseClient/supabase-client';


export type SearchAssetsResult = {
  items: AssetsSearchResult[];
  pagination: {
    page: number;
    pageSize: number;
    total?: number;
    totalPages?: number;
    hasNext?: boolean;
  }
  query: string
};

type SearchAssetsInput = {
  query?: string;
  page?: number;
  pageSize?: number;
  assetClass?: AssetClassCode;
};

const FUNCTION_NAME = import.meta.env.VITE_SEARCH_ASSETS_FUNCTION ?? 'search-assets';
const ASSET_DETAILS_FUNCTION = import.meta.env.VITE_ASSET_DETAILS_FUNCTION ?? 'assets';
const DEFAULT_PAGE_SIZE = 24;

const toNumber = (value: unknown, fallback = Number.NaN) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const searchAssetsService = {
  async search(input: SearchAssetsInput): Promise<SearchAssetsResult> {
    const page = Math.max(1, Math.floor(toNumber(input.page, 1)));
    const pageSize = Math.max(1, Math.floor(toNumber(input.pageSize, DEFAULT_PAGE_SIZE)));
    const query = input.query?.trim();
    const assetClass = input.assetClass;
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('page_size', String(pageSize));

    if (query) {
      params.set('q', query);
    }

    if (assetClass) {
      params.set('asset_class', assetClass);
    }

    const functionPath = params.toString() ? `${FUNCTION_NAME}?${params.toString()}` : FUNCTION_NAME;
    const { data, error } = await supabase.functions.invoke<SearchAssetsResult>(functionPath, { method: 'GET' });
    if (error) {
      throw error;
    }

    if (!data) {
      throw new Error("Not found")
    }

    return data
  },

  async getByTicker(ticker: string): Promise<Asset> {
    const normalized = ticker.trim().toUpperCase();

    const functionPath = `${ASSET_DETAILS_FUNCTION}/${encodeURIComponent(normalized)}`;
    const { data, error } = await supabase.functions.invoke<{data: Asset}>(functionPath, { method: 'GET' });
    if (error) {
      throw error;
    }

    if (!data) {
      throw new Error("Not found")
    }

    return data?.data
  },
};
